'use client';
import { useAppKitAccount, useDisconnect } from '@reown/appkit/react';
import { useEffect, useRef } from 'react';

/**
 * AppKit doesn't let us "just connect" - it opens a window first where the user has to select a wallet before we are able to trigger our internal connection logic. This hook handles abstracting that away.
 *
 * Takes a callback that will be called when the user has connected with AppKit.
 *
 * Returns a function that should be called when the user wants to connect with AppKit.
 *
 * @returns
 */
const useTryConnectAppKit = (onConnectedCb: () => void) => {
	const { isConnected } = useAppKitAccount();
	const { disconnect } = useDisconnect();

	const hasRunInitialDisconnectRef = useRef(false);

	// Run internal connection logic when we detect that the appkit state becomes connected (after we've already manually disconnected)
	useEffect(() => {
		if (isConnected && hasRunInitialDisconnectRef.current) {
			onConnectedCb();
		}
	}, [isConnected, onConnectedCb]);

	// Manually disconnect when the app starts (don't support auto-connect for now)
	// No idea why, but this code is needed to CONNECT to AppKit
	useEffect(() => {
		if (hasRunInitialDisconnectRef.current) return;
		disconnect();
		hasRunInitialDisconnectRef.current = true;
	}, []);
};

export default useTryConnectAppKit;
